import React from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getPopularSearches } from '../../services'
import TrendingUpIcons from '@/icons/TrendingUpIcons'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface PopularSearchesProps {
    onSearchClick: (query: string) => void
    className?: string
    maxItems?: number
    showHeader?: boolean
}

const PopularSearches: React.FC<PopularSearchesProps> = ({
    onSearchClick,
    className = '',
    maxItems = 8,
    showHeader = true,
}) => {
    const { t } = useTranslation('search')

    const {
        data: popularSearches = [],
        isLoading,
        error,
    } = useQueryWithHandler<string[]>(
        {
            queryKey: [QUERY_KEYS.SEARCH_POPULAR_SEARCHES],
            queryFn: getPopularSearches,
            staleTime: 1000 * 60 * 30, // 30 minutes
            retry: 1, // Only retry once
        },
        { showLoading: false, showError: false }
    )

    if (isLoading) {
        return (
            <div className={`p-4 ${className}`}>
                <div className="animate-pulse space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="flex flex-wrap gap-2">
                        {[...Array(6)].map((_, i) => (
                            <div
                                key={i}
                                className="h-6 bg-gray-100 rounded-full w-20"
                            ></div>
                        ))}
                    </div>
                </div>
            </div>
        )
    }

    if (error || popularSearches.length === 0) {
        return null
    }

    const displayItems = popularSearches.slice(0, maxItems)

    return (
        <div className={`${className}`}>
            {showHeader && (
                <div className="p-3 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center gap-2">
                        <TrendingUpIcons className="w-4 h-4 text-green-500" />
                        <Typogaphy className="text-sm font-medium text-gray-700">
                            {t('popular-searches')} ({popularSearches.length})
                        </Typogaphy>
                    </div>
                </div>
            )}

            <div className="p-4">
                <div className="flex flex-wrap gap-2">
                    {displayItems.map((searchQuery, index) => (
                        <button
                            key={index}
                            onClick={() => onSearchClick(searchQuery)}
                            className="inline-flex items-center px-3 py-1.5 text-sm bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 text-blue-700 hover:text-blue-800 rounded-full border border-blue-200 hover:border-blue-300 transition-all duration-200 group"
                        >
                            <span className="flex items-center gap-1.5">
                                <span className="text-xs opacity-60 group-hover:opacity-80">
                                    #{index + 1}
                                </span>
                                <span className="font-medium">
                                    {searchQuery}
                                </span>
                            </span>
                        </button>
                    ))}
                </div>

                {popularSearches.length > maxItems && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                        <Typogaphy className="text-xs text-gray-500 text-center">
                            {t('and-more-popular', {
                                count: popularSearches.length - maxItems,
                            })}
                        </Typogaphy>
                    </div>
                )}
            </div>
        </div>
    )
}

export default PopularSearches
